import Dexie, { type EntityTable } from "dexie";

// ─── Types offline (miroir du schéma Prisma + champs sync) ──

export type SyncStatus = "synced" | "pending" | "error";

export interface OfflineSite {
  id: string;
  name: string;
  client?: string | null;
  address?: string | null;
  userId: string;
  createdAt: Date;
  updatedAt: Date;
  syncStatus: SyncStatus;
  /** Nb d'échecs de push consécutifs (C5) : cap les retries auto pour éviter
   *  la boucle d'erreur silencieuse (ex. FK violation pin/mesure). */
  retryCount?: number;
}

export interface OfflineZone {
  id: string;
  name: string;
  planUrl?: string | null;
  planType: string; // "image" | "pdf" | "default"
  planBlob?: ArrayBuffer; // Image du plan stockée offline (ArrayBuffer pour compatibilité Safari iOS)
  planSyncStatus?: "synced" | "pending"; // Statut de sync du planBlob vers le serveur
  planRotation?: 0 | 90 | 180 | 270; // Rotation d'affichage du plan (local uniquement)
  locked?: boolean; // Verrou UX réversible (empêche ajout/déplacement de pins)
  order?: number; // Ordre d'affichage (drag-and-drop), respecté dans le PDF. Trié order asc, tiebreaker createdAt asc.
  siteId: string;
  createdAt: Date;
  updatedAt: Date;
  syncStatus: SyncStatus;
  /** Nb d'échecs de push consécutifs (C5) : cap les retries auto pour éviter
   *  la boucle d'erreur silencieuse (ex. FK violation pin/mesure). */
  retryCount?: number;
}

/** Ordre d'affichage des zones : `order` asc, puis `createdAt` asc comme
 *  tiebreaker stable (zones legacy encore à 0, ou jamais réordonnées).
 *  Source unique du tri, partagée par l'UI (useZones) et l'export PDF. */
export function compareZoneOrder(a: OfflineZone, b: OfflineZone): number {
  const byOrder = (a.order ?? 0) - (b.order ?? 0);
  if (byOrder !== 0) return byOrder;
  return a.createdAt.getTime() - b.createdAt.getTime();
}

export interface OfflineCampagne {
  id: string;
  date: Date;
  status: string; // "active" | "cloturee"
  notes?: string | null;
  siteId: string; // Modèle B : campagne au niveau site (refactor sprint 8)
  createdAt: Date;
  updatedAt: Date;
  syncStatus: SyncStatus;
  /** Nb d'échecs de push consécutifs (C5) : cap les retries auto pour éviter
   *  la boucle d'erreur silencieuse (ex. FK violation pin/mesure). */
  retryCount?: number;
}

export interface OfflinePin {
  id: string;
  number: number;
  x: number;
  y: number;
  siteId: string; // Modèle B : pin au niveau site (persistant cross-campagne)
  zoneId: string; // Plan physique sur lequel le pin est placé
  createdAt: Date;
  updatedAt: Date;
  syncStatus: SyncStatus;
  /** Nb d'échecs de push consécutifs (C5) : cap les retries auto pour éviter
   *  la boucle d'erreur silencieuse (ex. FK violation pin/mesure). */
  retryCount?: number;
}

export interface OfflineMesure {
  id: string;
  numero: number;
  // Champs génériques (core)
  photos: string[];
  notes?: string | null;
  // Champs métier corrosion (module)
  reseau?: string | null;
  /** Champ legacy "Réseau libre" : remplacé par la triade structurée ci-dessous.
   *  Conservé pour compat : mesures anciennes, fallback d'affichage. */
  localisation?: string | null;
  /** Type de réseau structuré (select 1) : stocke le libellé complet (ex: "Eau glacée") */
  reseauType?: string | null;
  /** Complément libre à côté du select (ex: "sous-sol étage 2") */
  reseauComplement?: string | null;
  /** Sens du réseau (select 2 conditionnel) : "Aller" | "Retour" : uniquement pour
   *  certains types (cf. RESEAU_TYPES_WITH_SENS) */
  reseauSens?: string | null;
  diametreExt?: number | null;
  dn?: number | null;
  orientation?: string | null; // "H" | "V"
  materiau?: string | null;
  fonteAge?: string | null;   // "Vieux" | "Récent"
  fonteType?: string | null;  // "SME" | "SMU"
  tarification?: string[] | null; // Multi-sélection
  mesureMax?: number | null;
  mesureMin?: number | null;
  epaisseurOrigine?: number | null;
  /** true = épaisseur d'origine saisie à la main, l'abaque ne l'écrase plus */
  epaisseurManuelle?: boolean;
  /** true = DN saisi à la main (fonte), prime sur le DN déduit du ∅ ext */
  dnManuel?: boolean;
  pourcentageCorrosion?: number | null;
  codeCouleur?: string | null; // "green" | "orange" | "red" | "dark"
  pinId: string;
  campagneId: string; // Modèle B : mesure liée à une (pin, campagne) unique
  createdAt: Date;
  updatedAt: Date;
  syncStatus: SyncStatus;
  /** Nb d'échecs de push consécutifs (C5) : cap les retries auto pour éviter
   *  la boucle d'erreur silencieuse (ex. FK violation pin/mesure). */
  retryCount?: number;
}

export interface OfflinePhoto {
  id: string;
  mesureId: string;
  blob: Blob;
  fileName: string;
  serverUrl?: string;
  syncStatus: SyncStatus;
}

export interface CustomSection {
  id: string;            // crypto.randomUUID()
  title: string;         // Titre éditable
  content?: unknown;     // Tiptap JSON
  afterSection: string;  // "couverture" | "mission" | "methodologie" | "mesures" | "conclusions"
  type?: "text" | "photo-analysis" | "pdf-annexe"; // défaut: "text"
  photoBlob?: Blob;      // Photo compressée (type photo-analysis)
  pdfAnnexeId?: string;  // ID de l'OfflineRapportAnnexe liée (type pdf-annexe)
  order?: number;        // Ordre dans le groupe afterSection (défaut: 0)
}

export interface OfflineRapport {
  id: string;
  numero: string;
  missionText?: unknown; // Tiptap JSON
  methodologieText?: unknown; // Tiptap JSON
  conclusionsText?: unknown; // Tiptap JSON
  customSections?: CustomSection[];
  /** Clés de sections fixes masquees par l'utilisateur (couverture, mission,
   *  methodologie, mesures, conclusions, synthese). Masquage non destructif :
   *  le contenu reste en BDD, simplement non rendu dans editeur et PDF. */
  hiddenSections?: string[];
  destinataire?: string | null;
  destinataireAdresse?: string | null;
  signataireNom?: string | null;
  signataireFonction?: string | null;
  /** Titre principal de la couverture (éditable, valeur par défaut pré-remplie côté UI) */
  titreCouverture?: string | null;
  /** Sous-titre réseau affiché sur la couverture (ex: "Réseaux change-over ventilos") */
  sousTitreReseau?: string | null;
  /** Bâtiment spécifique dans le site (ex: "Bâtiment Mollien") */
  batiment?: string | null;
  /** Nota Bene sous la signature (texte libre 2-3 lignes) */
  notaBene?: string | null;
  siteId: string;
  createdAt: Date;
  updatedAt: Date;
  syncStatus: SyncStatus;
  /** Nb d'échecs de push consécutifs (C5) : cap les retries auto pour éviter
   *  la boucle d'erreur silencieuse (ex. FK violation pin/mesure). */
  retryCount?: number;
}

export interface OfflinePendingDeletion {
  id?: number;            // auto-increment
  entityType: string;     // "site" | "zone" | "campagne" | "pin" | "mesure"
  entityId: string;
  deletedAt: Date;
  syncStatus: SyncStatus;
}

export interface OfflineRapportAnnexe {
  id: string;
  rapportId: string;
  fileName: string;
  fileSize: number;
  order: number;
  /** ID de la CustomSection liée (sprint annexe-anywhere). Null pour les
   *  annexes orphelines (legacy) qui seront migrées automatiquement. */
  customSectionId?: string | null;
  blob?: Blob;            // PDF stocké localement (offline)
  serverUrl?: string;     // URL distante une fois uploadé
  syncStatus: SyncStatus;
  /** Nb d'échecs d'upload consécutifs (C5) : cap les retries auto. */
  retryCount?: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface OfflineLibrarySection {
  id: string;
  title: string;
  content: unknown;       // Tiptap JSON
  category: string;       // "analyses-eau" | "principe-mesure" | "rappel-normes" | "mission" | "conclusions" | "custom"
  order: number;
  isGlobal: boolean;      // true = seed partagé visible par tous
  userId?: string | null;
  createdAt: Date;
  updatedAt: Date;
}

// ─── Base Dexie ─────────────────────────────────────────────

export class UltraMesureDB extends Dexie {
  sites!: EntityTable<OfflineSite, "id">;
  zones!: EntityTable<OfflineZone, "id">;
  campagnes!: EntityTable<OfflineCampagne, "id">;
  pins!: EntityTable<OfflinePin, "id">;
  mesures!: EntityTable<OfflineMesure, "id">;
  photos!: EntityTable<OfflinePhoto, "id">;
  rapports!: EntityTable<OfflineRapport, "id">;
  pendingDeletions!: EntityTable<OfflinePendingDeletion, "id">;
  librarySections!: EntityTable<OfflineLibrarySection, "id">;
  rapportAnnexes!: EntityTable<OfflineRapportAnnexe, "id">;

  constructor() {
    super("ultra-mesure");

    // v1 : schéma initial
    this.version(1).stores({
      sites: "id, userId, syncStatus",
      zones: "id, siteId, syncStatus",
      campagnes: "id, zoneId, syncStatus",
      pins: "id, campagneId, syncStatus",
      mesures: "id, pinId, syncStatus",
      photos: "id, mesureId, syncStatus",
    });

    // v2 : ajout planSyncStatus (champ non-indexé, pas de changement de stores)
    this.version(2).stores({
      sites: "id, userId, syncStatus",
      zones: "id, siteId, syncStatus",
      campagnes: "id, zoneId, syncStatus",
      pins: "id, campagneId, syncStatus",
      mesures: "id, pinId, syncStatus",
      photos: "id, mesureId, syncStatus",
    });

    // v3 : table pendingDeletions pour tracker les suppressions offline
    this.version(3).stores({
      sites: "id, userId, syncStatus",
      zones: "id, siteId, syncStatus",
      campagnes: "id, zoneId, syncStatus",
      pins: "id, campagneId, syncStatus",
      mesures: "id, pinId, syncStatus",
      photos: "id, mesureId, syncStatus",
      pendingDeletions: "++id, entityId, syncStatus",
    });

    // v4 : purge des campagnes orphelines héritées d'un bug pre-migration Vite.
    // Certaines campagnes avaient zoneId === "new" / "nouveau" (la chaîne littérale
    // du paramètre d'URL) au lieu d'un vrai ID de zone, ce qui faisait échouer la
    // sync push avec Foreign key constraint violation.
    // Attention : les IDs de zone ne sont PAS toujours des UUIDs (la seed data
    // utilise des slugs comme "zone-demo-1", "zone-1a"). On cible donc uniquement
    // les valeurs littérales problématiques identifiées en production.
    this.version(4).stores({
      sites: "id, userId, syncStatus",
      zones: "id, siteId, syncStatus",
      campagnes: "id, zoneId, syncStatus",
      pins: "id, campagneId, syncStatus",
      mesures: "id, pinId, syncStatus",
      photos: "id, mesureId, syncStatus",
      pendingDeletions: "++id, entityId, syncStatus",
    }).upgrade(async (tx) => {
      const INVALID_ZONE_IDS = new Set(["new", "nouveau", "undefined", "null", ""]);
      const campagnes = tx.table("campagnes");
      const orphanIds: string[] = [];
      await campagnes.each((c: { id: string; zoneId?: unknown }) => {
        if (typeof c.zoneId !== "string" || INVALID_ZONE_IDS.has(c.zoneId)) {
          orphanIds.push(c.id);
        }
      });
      if (orphanIds.length > 0) {
        await campagnes.bulkDelete(orphanIds);
        console.warn(`[UltraMesureDB v4] Purgé ${orphanIds.length} campagne(s) orpheline(s)`);
      }
    });

    // v5 : renumérotation des pins par site (numérotation continue cross-zones)
    // Tri par date de création, mise à jour du numero des mesures associées
    this.version(5).stores({
      sites: "id, userId, syncStatus",
      zones: "id, siteId, syncStatus",
      campagnes: "id, zoneId, syncStatus",
      pins: "id, campagneId, syncStatus",
      mesures: "id, pinId, syncStatus",
      photos: "id, mesureId, syncStatus",
      pendingDeletions: "++id, entityId, syncStatus",
    }).upgrade(async (tx) => {
      const sites = tx.table("sites");
      const zones = tx.table("zones");
      const campagnes = tx.table("campagnes");
      const pins = tx.table("pins");
      const mesures = tx.table("mesures");

      const allSites = await sites.toArray();
      let totalChanged = 0;

      for (const site of allSites) {
        // Récupérer tous les pins du site
        const siteZones = await zones.where("siteId").equals(site.id).toArray();
        const zoneIds = siteZones.map((z: { id: string }) => z.id);
        if (zoneIds.length === 0) continue;

        const siteCampagnes = await campagnes.where("zoneId").anyOf(zoneIds).toArray();
        const campagneIds = siteCampagnes.map((c: { id: string }) => c.id);
        if (campagneIds.length === 0) continue;

        const sitePins = await pins.where("campagneId").anyOf(campagneIds).toArray();
        if (sitePins.length === 0) continue;

        // Tri chronologique par date de création
        sitePins.sort(
          (a: { createdAt: Date }, b: { createdAt: Date }) =>
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
        );

        const now = new Date();
        for (let i = 0; i < sitePins.length; i++) {
          const newNumber = i + 1;
          const pin = sitePins[i] as { id: string; number: number };
          if (pin.number === newNumber) continue;

          await pins.update(pin.id, {
            number: newNumber,
            updatedAt: now,
            syncStatus: "pending",
          });

          // Mettre à jour le numero des mesures associées
          const pinMesures = await mesures.where("pinId").equals(pin.id).toArray();
          for (const m of pinMesures as { id: string }[]) {
            await mesures.update(m.id, {
              numero: newNumber,
              updatedAt: now,
              syncStatus: "pending",
            });
          }

          totalChanged++;
        }
      }

      if (totalChanged > 0) {
        console.warn(`[UltraMesureDB v5] Renuméroté ${totalChanged} pin(s) en numérotation continue par site`);
      }
    });

    // v6 : ajout table rapports (1 rapport par site, sections Tiptap pour le PDF)
    this.version(6).stores({
      sites: "id, userId, syncStatus",
      zones: "id, siteId, syncStatus",
      campagnes: "id, zoneId, syncStatus",
      pins: "id, campagneId, syncStatus",
      mesures: "id, pinId, syncStatus",
      photos: "id, mesureId, syncStatus",
      rapports: "id, siteId, syncStatus",
      pendingDeletions: "++id, entityId, syncStatus",
    });

    // v7 : refactor modèle B (sprint 8)
    // - Campagne : zoneId → siteId (une campagne = une visite d'un site)
    // - Pin : campagneId → siteId + zoneId (pin persistant au niveau site)
    // - Mesure : ajout campagneId (mesure = snapshot par (pin, campagne))
    // Migration : backfill les nouveaux champs depuis les relations existantes.
    this.version(7).stores({
      sites: "id, userId, syncStatus",
      zones: "id, siteId, syncStatus",
      campagnes: "id, siteId, syncStatus",
      pins: "id, siteId, zoneId, syncStatus",
      mesures: "id, pinId, campagneId, syncStatus",
      photos: "id, mesureId, syncStatus",
      rapports: "id, siteId, syncStatus",
      pendingDeletions: "++id, entityId, syncStatus",
    }).upgrade(async (tx) => {
      const zones = tx.table("zones");
      const campagnes = tx.table("campagnes");
      const pins = tx.table("pins");
      const mesures = tx.table("mesures");

      const now = new Date();

      // 1. Indexer zones par id pour lookup rapide
      const allZones = await zones.toArray();
      const zoneById = new Map<string, { id: string; siteId: string }>();
      for (const z of allZones as Array<{ id: string; siteId: string }>) {
        zoneById.set(z.id, { id: z.id, siteId: z.siteId });
      }

      // 2. Indexer campagnes : backfill siteId via leur ancienne zoneId
      const allCampagnes = await campagnes.toArray();
      const campagneOldZoneId = new Map<string, string>(); // campagneId → ancien zoneId
      for (const c of allCampagnes as Array<{ id: string; zoneId?: string; siteId?: string }>) {
        const oldZoneId = c.zoneId;
        if (!oldZoneId) continue;
        const z = zoneById.get(oldZoneId);
        if (!z) {
          // Campagne orpheline : on la garde mais elle sera cassée. Log.
          console.warn(`[Dexie v7] Campagne ${c.id} a un zoneId ${oldZoneId} inconnu, siteId non défini`);
          continue;
        }
        campagneOldZoneId.set(c.id, oldZoneId);
        await campagnes.update(c.id, {
          siteId: z.siteId,
          zoneId: undefined, // supprime l'ancien champ
          updatedAt: now,
          syncStatus: "pending",
        });
      }

      // 3. Pins : backfill siteId et zoneId via leur campagne d'origine
      const allPins = await pins.toArray();
      const pinNewCampagneMap = new Map<string, string>(); // pinId → campagneId d'origine (pour les mesures)
      for (const p of allPins as Array<{ id: string; campagneId?: string }>) {
        const oldCampagneId = p.campagneId;
        if (!oldCampagneId) continue;
        const oldZoneId = campagneOldZoneId.get(oldCampagneId);
        if (!oldZoneId) {
          console.warn(`[Dexie v7] Pin ${p.id} rattaché à campagne ${oldCampagneId} non migrée`);
          continue;
        }
        const z = zoneById.get(oldZoneId);
        if (!z) continue;
        pinNewCampagneMap.set(p.id, oldCampagneId);
        await pins.update(p.id, {
          siteId: z.siteId,
          zoneId: z.id,
          campagneId: undefined, // supprime l'ancien champ
          updatedAt: now,
          syncStatus: "pending",
        });
      }

      // 4. Mesures : backfill campagneId via leur pin
      const allMesures = await mesures.toArray();
      for (const m of allMesures as Array<{ id: string; pinId: string }>) {
        const campagneId = pinNewCampagneMap.get(m.pinId);
        if (!campagneId) {
          console.warn(`[Dexie v7] Mesure ${m.id} rattachée à pin ${m.pinId} non migré`);
          continue;
        }
        await mesures.update(m.id, {
          campagneId,
          updatedAt: now,
          syncStatus: "pending",
        });
      }

      console.warn(`[Dexie v7] Migration modèle B : ${allCampagnes.length} campagnes, ${allPins.length} pins, ${allMesures.length} mesures`);
    });

    // v8 : ajout zone.locked (verrou UX réversible par zone)
    // Le champ est optionnel côté TS, pas besoin de backfill explicite : undefined = non verrouillé.
    this.version(8).stores({
      sites: "id, userId, syncStatus",
      zones: "id, siteId, syncStatus",
      campagnes: "id, siteId, syncStatus",
      pins: "id, siteId, zoneId, syncStatus",
      mesures: "id, pinId, campagneId, syncStatus",
      photos: "id, mesureId, syncStatus",
      rapports: "id, siteId, syncStatus",
      pendingDeletions: "++id, entityId, syncStatus",
    });

    // v9 : fix post-migration v7 : la v7 avait créé des "campagnes actives zombies"
    // car l'ancien modèle avait 1 active par zone. Après déplacement de zoneId→siteId,
    // un site pouvait donc avoir plusieurs campagnes "active" simultanées.
    // Règle : pour chaque site, on garde la PLUS RÉCENTE active, on clôture les autres.
    this.version(9).stores({
      sites: "id, userId, syncStatus",
      zones: "id, siteId, syncStatus",
      campagnes: "id, siteId, syncStatus",
      pins: "id, siteId, zoneId, syncStatus",
      mesures: "id, pinId, campagneId, syncStatus",
      photos: "id, mesureId, syncStatus",
      rapports: "id, siteId, syncStatus",
      pendingDeletions: "++id, entityId, syncStatus",
    }).upgrade(async (tx) => {
      // NB : v9 upgrade : conservé tel quel (voir plus bas)
      const campagnes = tx.table("campagnes");
      const all = await campagnes.toArray();

      // Indexer les campagnes actives par siteId
      const activeBySite = new Map<string, Array<{ id: string; date: Date | string; createdAt: Date | string }>>();
      for (const c of all as Array<{ id: string; siteId: string; status: string; date: Date | string; createdAt: Date | string }>) {
        if (c.status !== "active") continue;
        const list = activeBySite.get(c.siteId) ?? [];
        list.push({ id: c.id, date: c.date, createdAt: c.createdAt });
        activeBySite.set(c.siteId, list);
      }

      const now = new Date();
      let zombiesClosed = 0;
      for (const [, actives] of activeBySite) {
        if (actives.length <= 1) continue;

        // Tri : plus récent d'abord (par date, puis createdAt en cas d'égalité)
        actives.sort((a, b) => {
          const da = new Date(a.date).getTime();
          const db = new Date(b.date).getTime();
          if (da !== db) return db - da;
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        });

        // Le premier reste actif, les suivants passent en "cloturee"
        for (let i = 1; i < actives.length; i++) {
          await campagnes.update(actives[i].id, {
            status: "cloturee",
            updatedAt: now,
            syncStatus: "pending",
          });
          zombiesClosed++;
        }
      }

      if (zombiesClosed > 0) {
        console.warn(`[Dexie v9] Fix zombies : ${zombiesClosed} campagne(s) active(s) excédentaire(s) clôturée(s)`);
      }
    });

    // v10 : Réseau structuré (select type + complément + sens).
    // Backfill : on copie l'ancien `localisation` dans `reseauComplement` pour les mesures
    // qui n'ont pas encore de type structuré. `localisation` reste renseigné (fallback d'affichage).
    this.version(10).stores({
      sites: "id, userId, syncStatus",
      zones: "id, siteId, syncStatus",
      campagnes: "id, siteId, syncStatus",
      pins: "id, siteId, zoneId, syncStatus",
      mesures: "id, pinId, campagneId, syncStatus",
      photos: "id, mesureId, syncStatus",
      rapports: "id, siteId, syncStatus",
      pendingDeletions: "++id, entityId, syncStatus",
    }).upgrade(async (tx) => {
      const mesures = tx.table("mesures");
      const all = await mesures.toArray() as Array<{
        id: string;
        localisation?: string | null;
        reseauType?: string | null;
        reseauComplement?: string | null;
      }>;
      let backfilled = 0;
      for (const m of all) {
        if (m.reseauType) continue;
        if (m.reseauComplement) continue;
        if (!m.localisation) continue;
        await mesures.update(m.id, { reseauComplement: m.localisation });
        backfilled++;
      }
      if (backfilled > 0) {
        console.warn(`[Dexie v10] Réseau structuré : ${backfilled} mesure(s) backfill (localisation → reseauComplement)`);
      }
    });

    // v11 : couverture rapport enrichie (sousTitreReseau, batiment, notaBene)
    // Champs optionnels, aucun backfill nécessaire.
    this.version(11).stores({
      sites: "id, userId, syncStatus",
      zones: "id, siteId, syncStatus",
      campagnes: "id, siteId, syncStatus",
      pins: "id, siteId, zoneId, syncStatus",
      mesures: "id, pinId, campagneId, syncStatus",
      photos: "id, mesureId, syncStatus",
      rapports: "id, siteId, syncStatus",
      pendingDeletions: "++id, entityId, syncStatus",
    });

    // v12 : titre de couverture rapport éditable (titreCouverture)
    // Champ optionnel, aucun backfill. Le PDF utilise un fallback si vide.
    this.version(12).stores({
      sites: "id, userId, syncStatus",
      zones: "id, siteId, syncStatus",
      campagnes: "id, siteId, syncStatus",
      pins: "id, siteId, zoneId, syncStatus",
      mesures: "id, pinId, campagneId, syncStatus",
      photos: "id, mesureId, syncStatus",
      rapports: "id, siteId, syncStatus",
      pendingDeletions: "++id, entityId, syncStatus",
    });

    // v13 : table librarySections : blocs de contenu réutilisables (Évolution 2)
    // isGlobal = true → seeds partagés ; false → blocs personnels de l'utilisateur.
    this.version(13).stores({
      sites: "id, userId, syncStatus",
      zones: "id, siteId, syncStatus",
      campagnes: "id, siteId, syncStatus",
      pins: "id, siteId, zoneId, syncStatus",
      mesures: "id, pinId, campagneId, syncStatus",
      photos: "id, mesureId, syncStatus",
      rapports: "id, siteId, syncStatus",
      pendingDeletions: "++id, entityId, syncStatus",
      librarySections: "id, category, isGlobal",
    });

    // v14 : table rapportAnnexes (PDF labo fusionnés en fin de rapport, Sprint 4)
    this.version(14).stores({
      sites: "id, userId, syncStatus",
      zones: "id, siteId, syncStatus",
      campagnes: "id, siteId, syncStatus",
      pins: "id, siteId, zoneId, syncStatus",
      mesures: "id, pinId, campagneId, syncStatus",
      photos: "id, mesureId, syncStatus",
      rapports: "id, siteId, syncStatus",
      pendingDeletions: "++id, entityId, syncStatus",
      librarySections: "id, category, isGlobal",
      rapportAnnexes: "id, rapportId, syncStatus",
    });

    // v15 : annexes PDF deviennent des sections libres insertables n'importe ou
    //       (champ customSectionId sur rapportAnnexes + type "pdf-annexe" sur CustomSection)
    //       Migration : creer une CustomSection pdf-annexe pour chaque annexe orpheline
    this.version(15).stores({
      sites: "id, userId, syncStatus",
      zones: "id, siteId, syncStatus",
      campagnes: "id, siteId, syncStatus",
      pins: "id, siteId, zoneId, syncStatus",
      mesures: "id, pinId, campagneId, syncStatus",
      photos: "id, mesureId, syncStatus",
      rapports: "id, siteId, syncStatus",
      pendingDeletions: "++id, entityId, syncStatus",
      librarySections: "id, category, isGlobal",
      rapportAnnexes: "id, rapportId, customSectionId, syncStatus",
    }).upgrade(async (tx) => {
      const rapports = await tx.table("rapports").toArray();
      for (const rapport of rapports) {
        const annexes = await tx.table("rapportAnnexes")
          .where("rapportId").equals(rapport.id)
          .toArray();
        const orphans = annexes.filter((a) => !a.customSectionId);
        if (orphans.length === 0) continue;

        orphans.sort((a, b) => a.order - b.order);
        const customSections: CustomSection[] = [...(rapport.customSections ?? [])];
        let nextOrder =
          Math.max(
            0,
            ...customSections
              .filter((s) => s.afterSection === "conclusions")
              .map((s) => s.order ?? 0)
          ) + 1;

        for (const annexe of orphans) {
          const sectionId = crypto.randomUUID();
          customSections.push({
            id: sectionId,
            type: "pdf-annexe",
            title: annexe.fileName,
            pdfAnnexeId: annexe.id,
            afterSection: "conclusions",
            order: nextOrder++,
          });
          await tx.table("rapportAnnexes").update(annexe.id, {
            customSectionId: sectionId,
          });
        }

        await tx.table("rapports").update(rapport.id, { customSections });
      }
    });
  }
}

export const offlineDb = new UltraMesureDB();

// ─── Cascade delete helpers (Dexie n'a pas de cascade auto) ──

/** Supprime un site et toutes ses données enfants (zones, campagnes, pins, mesures, photos, rapport) */
export async function deleteSiteCascade(siteId: string) {
  return offlineDb.transaction(
    "rw",
    [offlineDb.sites, offlineDb.zones, offlineDb.campagnes, offlineDb.pins, offlineDb.mesures, offlineDb.photos, offlineDb.rapports],
    async () => {
      const zones = await offlineDb.zones.where("siteId").equals(siteId).toArray();
      for (const zone of zones) {
        await deleteZoneCascadeInner(zone.id);
      }
      await offlineDb.rapports.where("siteId").equals(siteId).delete();
      await offlineDb.sites.delete(siteId);
    }
  );
}

/** Supprime une zone et toutes ses données enfants */
export async function deleteZoneCascade(zoneId: string) {
  return offlineDb.transaction(
    "rw",
    [offlineDb.zones, offlineDb.campagnes, offlineDb.pins, offlineDb.mesures, offlineDb.photos],
    async () => {
      await deleteZoneCascadeInner(zoneId);
    }
  );
}

/** Supprime une campagne et toutes ses mesures + photos (modèle B).
 *  Les pins ne sont PAS supprimés (ils sont au niveau site, persistants). */
export async function deleteCampagneCascade(campagneId: string) {
  return offlineDb.transaction(
    "rw",
    [offlineDb.campagnes, offlineDb.mesures, offlineDb.photos],
    async () => {
      const mesures = await offlineDb.mesures.where("campagneId").equals(campagneId).toArray();
      for (const m of mesures) {
        await offlineDb.photos.where("mesureId").equals(m.id).delete();
      }
      await offlineDb.mesures.where("campagneId").equals(campagneId).delete();
      await offlineDb.campagnes.delete(campagneId);
    }
  );
}

/** Supprime un pin et ses mesures/photos (dans une transaction) */
export async function deletePinCascade(pinId: string) {
  return offlineDb.transaction(
    "rw",
    [offlineDb.pins, offlineDb.mesures, offlineDb.photos],
    async () => {
      const mesures = await offlineDb.mesures.where("pinId").equals(pinId).toArray();
      for (const m of mesures) {
        await offlineDb.photos.where("mesureId").equals(m.id).delete();
      }
      await offlineDb.mesures.where("pinId").equals(pinId).delete();
      await offlineDb.pins.delete(pinId);
    }
  );
}

/** Supprime une mesure et ses photos (dans une transaction) */
export async function deleteMesureCascade(mesureId: string) {
  return offlineDb.transaction(
    "rw",
    [offlineDb.mesures, offlineDb.photos],
    async () => {
      await offlineDb.photos.where("mesureId").equals(mesureId).delete();
      await offlineDb.mesures.delete(mesureId);
    }
  );
}

// ─── Garde anti data-loss (C4) ─────────────────────────────
// Une cascade-delete déclenchée par un PULL (suppression côté serveur ou
// absence dans un full-pull) ne doit JAMAIS détruire des données locales
// non encore synchronisées (pending/error). Sinon on perd des mesures du
// terrain : bug confirmé EUTELSAT / 2026-06-11.

export type CascadeEntityType = "site" | "zone" | "campagne" | "pin" | "mesure";

function isUnsynced(s: SyncStatus | undefined): boolean {
  return s === "pending" || s === "error";
}

/** Y a-t-il, dans le sous-arbre de cette entité, au moins un descendant
 *  non synchronisé (pending/error) : y compris les photos ? */
export async function hasUnsyncedDescendants(
  type: CascadeEntityType,
  id: string
): Promise<boolean> {
  switch (type) {
    case "mesure": {
      const photos = await offlineDb.photos.where("mesureId").equals(id).toArray();
      return photos.some((p) => isUnsynced(p.syncStatus));
    }
    case "pin": {
      const mesures = await offlineDb.mesures.where("pinId").equals(id).toArray();
      if (mesures.some((m) => isUnsynced(m.syncStatus))) return true;
      for (const m of mesures) if (await hasUnsyncedDescendants("mesure", m.id)) return true;
      return false;
    }
    case "campagne": {
      const mesures = await offlineDb.mesures.where("campagneId").equals(id).toArray();
      if (mesures.some((m) => isUnsynced(m.syncStatus))) return true;
      for (const m of mesures) if (await hasUnsyncedDescendants("mesure", m.id)) return true;
      return false;
    }
    case "zone": {
      const pins = await offlineDb.pins.where("zoneId").equals(id).toArray();
      if (pins.some((p) => isUnsynced(p.syncStatus))) return true;
      for (const p of pins) if (await hasUnsyncedDescendants("pin", p.id)) return true;
      return false;
    }
    case "site": {
      const zones = await offlineDb.zones.where("siteId").equals(id).toArray();
      if (zones.some((z) => isUnsynced(z.syncStatus))) return true;
      const campagnes = await offlineDb.campagnes.where("siteId").equals(id).toArray();
      if (campagnes.some((c) => isUnsynced(c.syncStatus))) return true;
      for (const c of campagnes) if (await hasUnsyncedDescendants("campagne", c.id)) return true;
      const pins = await offlineDb.pins.where("siteId").equals(id).toArray();
      if (pins.some((p) => isUnsynced(p.syncStatus))) return true;
      for (const p of pins) if (await hasUnsyncedDescendants("pin", p.id)) return true;
      return false;
    }
  }
}

/** Cascade-delete SEULEMENT si tout le sous-arbre est synchronisé.
 *  Retourne true si supprimé, false si préservé (données pending/error présentes). */
export async function cascadeDeleteIfFullySynced(
  type: CascadeEntityType,
  id: string
): Promise<boolean> {
  if (await hasUnsyncedDescendants(type, id)) {
    return false; // on PRÉSERVE : ne jamais détruire des données non synchronisées
  }
  switch (type) {
    case "site": await deleteSiteCascade(id); break;
    case "zone": await deleteZoneCascade(id); break;
    case "campagne": await deleteCampagneCascade(id); break;
    case "pin": await deletePinCascade(id); break;
    case "mesure": await deleteMesureCascade(id); break;
  }
  return true;
}

// ─── Invariant "1 campagne active par site" (I14) ──────────
/** Rend une campagne active de façon EXCLUSIVE : clôture toutes les autres
 *  campagnes "active" du même site, puis active la cible. Atomique.
 *  Utilisé à la réouverture d'une campagne (la création est déjà gardée). */
export async function setActiveCampagneExclusive(
  siteId: string,
  campagneId: string
): Promise<void> {
  await offlineDb.transaction("rw", offlineDb.campagnes, async () => {
    const others = await offlineDb.campagnes
      .where("siteId")
      .equals(siteId)
      .filter((c) => c.status === "active" && c.id !== campagneId)
      .toArray();
    const now = new Date();
    for (const o of others) {
      await offlineDb.campagnes.update(o.id, {
        status: "cloturee",
        updatedAt: now,
        syncStatus: "pending",
      });
    }
    await offlineDb.campagnes.update(campagneId, {
      status: "active",
      updatedAt: now,
      syncStatus: "pending",
    });
  });
}

// ─── Compteur d'entités bloquées (C5) ──────────────────────
// Entités en "error" qui ont dépassé le cap de retries : ne sont plus
// re-poussées automatiquement et doivent être remontées à l'utilisateur.
export const MAX_PUSH_RETRIES = 5;

export async function countStuckEntities(): Promise<number> {
  const tables = [
    offlineDb.sites,
    offlineDb.zones,
    offlineDb.campagnes,
    offlineDb.pins,
    offlineDb.mesures,
    offlineDb.rapportAnnexes,
  ];
  let stuck = 0;
  for (const table of tables) {
    const errored = await table.where("syncStatus").equals("error").toArray();
    stuck += errored.filter((e) => (e.retryCount ?? 0) >= MAX_PUSH_RETRIES).length;
  }
  return stuck;
}

/**
 * I5 : décide si une version serveur reçue au pull doit écraser l'entité locale.
 *
 * Règle anti-perte-d'édition concurrente : on n'écrase JAMAIS une entité
 * `pending`/`error` (édition faite offline, pas encore poussée au serveur). Sinon
 * un pull qui arrive avant le push efface silencieusement le travail terrain.
 * L'édition locale est conservée telle quelle et sera poussée au prochain cycle.
 *
 * Pour une entité `synced`, last-write-wins classique sur `updatedAt`.
 *
 * (Pendant des suppressions, `hasPendingDeletion` protège déjà l'autre sens.)
 */
export function shouldOverwriteFromServer(
  localSyncStatus: SyncStatus,
  localUpdatedAt: Date,
  serverUpdatedAt: Date
): boolean {
  if (localSyncStatus !== "synced") return false;
  return serverUpdatedAt > localUpdatedAt;
}

// ─── Helpers de conversion Blob ↔ ArrayBuffer ──────────────

/** Convertit un ArrayBuffer stocké dans Dexie en Blob image pour affichage */
export function arrayBufferToBlob(buffer: ArrayBuffer): Blob {
  return new Blob([buffer], { type: "image/jpeg" });
}

/** Convertit un Blob (depuis input file ou éditeur) en ArrayBuffer pour stockage Dexie */
export async function blobToArrayBuffer(blob: Blob): Promise<ArrayBuffer> {
  return blob.arrayBuffer();
}

// ─── Helper : enregistrer une suppression en attente de sync ──

/** Enregistre une suppression pour push ultérieur (quand offline) */
export async function recordPendingDeletion(
  entityType: string,
  entityId: string
): Promise<void> {
  await offlineDb.pendingDeletions.add({
    entityType,
    entityId,
    deletedAt: new Date(),
    syncStatus: "pending",
  });
}

// Helper interne (appelé depuis une transaction parent) : modèle B
// Zone → pins (site-level mais plan = la zone) → mesures → photos.
// Les campagnes sont au niveau site, PAS cascadées ici : elles persistent
// même si la zone où elles étaient mesurées est supprimée.
async function deleteZoneCascadeInner(zoneId: string) {
  const pins = await offlineDb.pins.where("zoneId").equals(zoneId).toArray();
  for (const pin of pins) {
    const mesures = await offlineDb.mesures.where("pinId").equals(pin.id).toArray();
    for (const m of mesures) {
      await offlineDb.photos.where("mesureId").equals(m.id).delete();
    }
    await offlineDb.mesures.where("pinId").equals(pin.id).delete();
  }
  await offlineDb.pins.where("zoneId").equals(zoneId).delete();
  await offlineDb.zones.delete(zoneId);
}
